import React from "react";
import { motion } from "framer-motion";
import { Quote, GraduationCap, ShieldCheck, Users } from "lucide-react";

const heroImage =
  "https://images.unsplash.com/photo-1448630360428-65456885c650?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=1500&q=80";

const credentials = [
  { icon: GraduationCap, label: "MBA, Finance" },
  { icon: ShieldCheck, label: "20+ years senior banking" },
  { icon: Users, label: "Network of expert associates" },
];

export const Founder = () => {
  return (
    <section id="founder" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-md overflow-hidden border border-brand-border bg-brand-surface">
              <img
                src={heroImage}
                alt="Glass corporate towers — symbolising the transformation from company to corporation"
                className="w-full h-full object-cover contrast-[1.05] saturate-[0.85]"
                loading="lazy"
              />
              <div className="absolute inset-0"
                   style={{ background: "linear-gradient(180deg, rgba(10,28,20,0.35) 0%, rgba(10,28,20,0.55) 55%, rgba(10,28,20,0.95) 100%)" }} />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="text-overline text-brand-accent">Company &nbsp;→&nbsp; Corporation</p>
                <p className="font-serif text-white text-2xl md:text-3xl mt-2 leading-tight tracking-tight">
                  The architecture of <em className="not-italic text-brand-accent">scale.</em>
                </p>
              </div>
            </div>
            <div
              aria-hidden
              className="absolute -z-10 -top-6 -left-6 w-40 h-40 rounded-full bg-brand-primary/20 blur-3xl"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <p className="text-overline text-brand-accent">Leadership</p>
            <h2 className="mt-4 font-serif text-white text-4xl md:text-5xl tracking-tight leading-tight">
              A senior banker, on every mandate.
            </h2>

            <div className="mt-6 relative pl-6 border-l-2 border-brand-accent/50">
              <Quote className="absolute -left-3 -top-2 w-5 h-5 text-brand-accent bg-[#0A1C14]" />
              <p className="font-serif italic text-white/85 text-lg md:text-xl leading-relaxed">
                Most founders don&rsquo;t need a bigger pitch deck — they need a sharper question.
                Our job is to sit across the table, ask it, and then build the structure that
                makes the answer fundable.
              </p>
              <p className="mt-3 text-sm text-white/55">— Jayaprakash Sahu, Founder</p>
            </div>

            <p className="mt-8 text-base text-white/70 leading-relaxed max-w-2xl">
              With over two decades across banking, insurance and SME relationship management,
              Jayaprakash has guided founders through capital raises, restructurings, valuations
              and M&amp;A across India&rsquo;s mid-market. Transformo extends that craft through a
              curated network of expert associates — chartered accountants, ex-bankers and
              sector specialists — assembled for the precise needs of every mandate.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {credentials.map((c) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.label}
                    className="flex items-center gap-3 rounded-md border border-brand-border bg-brand-surface px-4 py-3"
                  >
                    <Icon className="w-4 h-4 text-brand-accent" />
                    <span className="text-sm text-white/80">{c.label}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Founder;
